import React from "react";

// Sprint 99 — FLEX share rules in the Smart builder's simple area.
// Share of the portfolio whose FLEX slot is a 3rd RB / 4th WR / 2nd TE.
export const FLEX_POS = ["RB", "WR", "TE"];
export const FLEX_CAPS_DEFAULT = { RB: { min: 0, max: 100 }, WR: { min: 0, max: 100 }, TE: { min: 0, max: 100 } };

const clampPct = (v, fb) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : fb;
};

export const normalizeFlexCaps = (raw) => {
  const out = {};
  for (const p of FLEX_POS) {
    const v = (raw || {})[p] || {};
    out[p] = { min: clampPct(v.min, 0), max: clampPct(v.max, 100) };
  }
  return out;
};

export const flexCapsIssue = (caps) => {
  const b = normalizeFlexCaps(caps);
  for (const p of FLEX_POS) if (b[p].min > b[p].max) return `FLEX ${p}: min above max`;
  const sMin = FLEX_POS.reduce((s, p) => s + b[p].min, 0);
  const sMax = FLEX_POS.reduce((s, p) => s + b[p].max, 0);
  if (sMin > 100) return `FLEX minimums add up to ${sMin}% — must be ≤ 100%`;
  if (sMax < 100) return `FLEX maximums add up to ${sMax}% — must be ≥ 100%`;
  return null;
};

// Backend wants fractions of the lineup count. Returns null when the user
// left every position wide open (0–100) so the ruleset keeps its own mix.
export const flexCapsPayload = (caps) => {
  const b = normalizeFlexCaps(caps);
  const active = FLEX_POS.some((p) => b[p].min > 0 || b[p].max < 100);
  if (!active) return null;
  const out = {};
  for (const p of FLEX_POS) out[p] = { min: b[p].min / 100, max: b[p].max / 100 };
  return out;
};

const miniCls = "bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1 py-1 mono text-[11px] w-[44px] text-center";

export const FlexShareRules = ({ caps, onChange, disabled = false }) => {
  const b = normalizeFlexCaps(caps);
  const issue = flexCapsIssue(b);
  const upd = (pos, key, raw) => {
    const fallback = key === "max" ? 100 : 0;
    const v = raw === "" ? fallback : clampPct(raw, fallback);
    onChange({ ...b, [pos]: { ...b[pos], [key]: v } });
  };
  const touched = FLEX_POS.some((p) => b[p].min > 0 || b[p].max < 100);
  return (
    <div
      className="mt-3 border border-zinc-800 bg-[#0e0e0e] px-3 py-2.5"
      data-testid="flex-share-rules"
      title="Share of your lineups whose FLEX slot is a 3rd RB / 4th WR / 2nd TE. Leave 0–100 to let the ruleset pick the mix."
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className={`mono text-[9px] uppercase tracking-[0.2em] ${issue ? "text-[#FFCC00]" : "text-zinc-500"}`}>
            Flex share % · min–max
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">
            How much of the portfolio runs a 3rd RB, 4th WR or 2nd TE in the FLEX. 0–100 = no rule.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FLEX_POS.map((p) => (
            <div key={p} className="flex items-center gap-1" data-testid={`flex-share-${p}`}>
              <span className="mono text-[10px] font-black text-zinc-300 w-[20px]">{p}</span>
              <input
                type="number" min="0" max="100" disabled={disabled} className={miniCls} value={b[p].min}
                onChange={(e) => upd(p, "min", e.target.value)}
                data-testid={`flex-share-${p}-min`} aria-label={`FLEX ${p} min percent`}
              />
              <span className="text-zinc-600 mono text-[10px]">–</span>
              <input
                type="number" min="0" max="100" disabled={disabled} className={miniCls} value={b[p].max}
                onChange={(e) => upd(p, "max", e.target.value)}
                data-testid={`flex-share-${p}-max`} aria-label={`FLEX ${p} max percent`}
              />
            </div>
          ))}
          {touched && (
            <button
              type="button" disabled={disabled} data-testid="flex-share-reset"
              onClick={() => onChange({ ...FLEX_CAPS_DEFAULT })}
              className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 underline underline-offset-2"
            >
              reset
            </button>
          )}
        </div>
      </div>
      {issue && (
        <div className="mono text-[10px] text-[#FFCC00] mt-2" data-testid="flex-share-issue">{issue}</div>
      )}
    </div>
  );
};

export default FlexShareRules;
